import { grassTypeToGuideSlug } from '@/lib/grass-slug'

export type Season = 'spring' | 'summer' | 'fall' | 'winter'
export type ServiceLevel = 'basic' | 'standard' | 'premium'
export type CareApproach = 'diy' | 'professional' | 'hybrid'
export type HerbicideApplicationType = 'pre-emergent' | 'post-emergent' | 'both'

export interface ToolUrlState {
  sourceToolId?: string | null
  zip?: string | null
  location?: string | null
  grassType?: string | null
  grasses?: Array<string | null | undefined> | null
  lawnSize?: number | string | null
  soilType?: string | null
  sunlight?: string | null
  season?: Season | string | null
  fertilizerType?: string | null
  serviceLevel?: ServiceLevel | string | null
  careApproach?: CareApproach | string | null
  zones?: number | string | null
  catchCanMinutes?: number | string | null
  catchCanDepth?: number | string | null
  zoneDepths?: Array<number | string | null | undefined> | string | null
  applicationType?: HerbicideApplicationType | string | null
  targetWeeds?: string[] | null
  thatchBand?: string | null
  turfGrade?: string | null
  installMethod?: string | null
  removeExistingLawn?: boolean | null
}

const THATCH_BANDS = new Set(["under-quarter", "quarter-to-half", "half-to-one", "over-one"])

const TOOL_PATHS: Record<string, string> = {
  'lawn-size-calculator': '/tools/lawn-size-calculator',
  'soil-temperature': '/tools/soil-temperature',
  'seeding-calculator': '/tools/seeding-calculator',
  'fertilizer-calculator': '/tools/fertilizer-calculator',
  'watering-schedule': '/tools/watering-schedule',
  'lawn-cost-estimator': '/tools/lawn-cost-estimator',
  'mowing-calculator': '/tools/mowing-calculator',
  'aeration-timing-tool': '/tools/aeration-calculator',
  'herbicide-timing': '/tools/herbicide-timing',
  'soil-test-analyzer': '/tools/soil-test-analyzer',
  'grass-comparison': '/tools/grass-comparison',
  'tank-mix-calculator': '/tools/tank-mix-calculator',
  'fertilizer-rate-calculator': '/tools/fertilizer-rate-calculator',
  'sprinkler-cost-calculator': '/tools/sprinkler-cost-calculator',
  'sod-calculator': '/tools/sod-calculator',
  'dethatching-calculator': '/tools/dethatching-calculator',
  'artificial-turf-cost-calculator': '/tools/artificial-turf-cost-calculator',
}

function getCurrentSeasonForTool(): Season {
  const month = new Date().getMonth() // 0-11
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

function buildUrl(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, v)
  }
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

function toPositiveString(value: number | string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return String(Math.round(n))
}

function toNumberString(value: number | string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return String(n)
}

function toZip(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed && /^\d{5}$/.test(trimmed) ? trimmed : undefined
}

function toSeason(value: string | null | undefined): Season | undefined {
  const normalized = value?.toLowerCase()
  if (
    normalized === 'spring' ||
    normalized === 'summer' ||
    normalized === 'fall' ||
    normalized === 'winter'
  ) {
    return normalized
  }
  return undefined
}

function toSunlight(value: string | null | undefined): string | undefined {
  const normalized = value?.trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'full' || normalized === 'full_sun') return 'full_sun'
  if (normalized === 'partial' || normalized === 'partial_sun') return 'partial_sun'
  if (normalized === 'shade') return 'shade'
  return undefined
}

function toGrassSlug(value: string | null | undefined): string | undefined {
  return grassTypeToGuideSlug(value) ?? undefined
}

function toThatchBand(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed && THATCH_BANDS.has(trimmed) ? trimmed : undefined
}

function toGrassComparisonSlugs(opts: ToolUrlState): string | undefined {
  const slugs = (opts.grasses ?? [])
    .map((grass) => toGrassSlug(grass ?? undefined))
    .filter((grass): grass is string => Boolean(grass))

  const primary = toGrassSlug(opts.grassType ?? undefined)
  if (primary && !slugs.includes(primary)) slugs.unshift(primary)

  if (slugs.length === 1) {
    slugs.push(slugs[0] === 'kentucky-bluegrass' ? 'bermudagrass' : 'kentucky-bluegrass')
  }

  return slugs.length >= 2 ? slugs.slice(0, 3).join(',') : undefined
}

function toZoneDepths(value: ToolUrlState['zoneDepths']): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined
  if (!Array.isArray(value)) return undefined
  const normalized = value
    .map((depth) => {
      if (depth === null || depth === undefined || depth === '') return ''
      const n = typeof depth === 'number' ? depth : Number(depth)
      return Number.isFinite(n) && n > 0 ? String(n) : ''
    })
    .join(',')
  return normalized.replace(/,+$/g, '') || undefined
}

export function buildToolUrl(toolId: string, opts: ToolUrlState = {}): string {
  const path = TOOL_PATHS[toolId] ?? `/tools/${toolId}`
  const zip = toZip(opts.zip ?? undefined)
  const grass = toGrassSlug(opts.grassType ?? undefined)
  const size = toPositiveString(opts.lawnSize)
  const sunlight = toSunlight(opts.sunlight ?? undefined)
  const common = {
    fromTool: opts.sourceToolId ?? undefined,
  }

  switch (toolId) {
    case 'lawn-size-calculator':
      return buildUrl(path, {
        ...common,
        zip,
        size,
      })

    case 'soil-temperature':
      return buildUrl(path, {
        ...common,
        location: opts.location?.trim() || zip,
      })

    case 'grass-comparison':
      return buildUrl(path, {
        ...common,
        grasses: toGrassComparisonSlugs(opts),
        zip,
        sunlight,
      })

    case 'soil-test-analyzer':
      return buildUrl(path, { ...common, zip, grass, size })

    case 'fertilizer-calculator':
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        season: toSeason(opts.season ?? undefined),
        type: opts.fertilizerType ?? undefined,
        sunlight,
      })

    case 'watering-schedule':
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        soil: opts.soilType ?? undefined,
        sunlight,
        zones: toPositiveString(opts.zones),
        canMinutes: toNumberString(opts.catchCanMinutes),
        canDepth: toNumberString(opts.catchCanDepth),
        zoneDepths: toZoneDepths(opts.zoneDepths),
      })

    case 'lawn-cost-estimator':
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        level: opts.serviceLevel ?? undefined,
        approach: opts.careApproach ?? undefined,
        sunlight,
      })

    case 'mowing-calculator':
      return buildUrl(path, {
        ...common,
        grass,
        sunlight,
        season: toSeason(opts.season ?? undefined),
      })

    case 'aeration-timing-tool':
      return buildUrl(path, { ...common, zip, grass, size, sunlight })

    case 'seeding-calculator':
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        sunlight,
      })

    case 'sod-calculator':
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
      })

    case 'artificial-turf-cost-calculator':
      // Grass-less cost tool. Carry area (size), turf grade, install method,
      // and the removal toggle so a shared link reproduces the same estimate.
      return buildUrl(path, {
        ...common,
        zip,
        size,
        grade: opts.turfGrade ?? undefined,
        install: opts.installMethod ?? undefined,
        remove: opts.removeExistingLawn ? '1' : undefined,
      })

    case 'dethatching-calculator':
      // Carry the result-defining thatch band + lawn size so a shared link
      // reproduces the sender's exact verdict, not the sample default.
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        thatch: toThatchBand(opts.thatchBand),
      })

    case 'herbicide-timing':
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        sunlight,
        type: opts.applicationType ?? undefined,
        weeds: opts.targetWeeds?.length ? opts.targetWeeds.join(',') : undefined,
      })

    default:
      return buildUrl(path, {
        ...common,
        zip,
        grass,
        size,
        sunlight,
      })
  }
}

export function buildRelatedToolUrl(
  targetToolId: string,
  sourceToolId: string,
  prefill: ToolUrlState = {},
): string {
  return buildToolUrl(targetToolId, {
    ...prefill,
    sourceToolId,
  })
}

export function buildFertilizerToolUrl(opts: { grassType?: string | null; season?: Season } = {}): string {
  return buildToolUrl('fertilizer-calculator', {
    grassType: opts.grassType,
    season: opts.season ?? getCurrentSeasonForTool(),
  })
}

export function buildSoilTempToolUrl(opts: { zip?: string | null } = {}): string {
  return buildToolUrl('soil-temperature', { zip: opts.zip })
}

export function buildHerbicideTimingToolUrl(opts: {
  grassType?: string | null;
  zip?: string | null;
  applicationType?: HerbicideApplicationType | string | null;
  targetWeeds?: string[] | null;
} = {}): string {
  return buildToolUrl('herbicide-timing', {
    grassType: opts.grassType,
    zip: opts.zip,
    applicationType: opts.applicationType,
    targetWeeds: opts.targetWeeds,
  })
}

export function buildWateringScheduleUrl(
  opts: Pick<ToolUrlState, 'grassType' | 'zip' | 'lawnSize' | 'soilType' | 'sunlight' | 'zones'> = {},
): string {
  return buildToolUrl('watering-schedule', opts)
}
